import React from "react";
import { Link } from "react-router-dom";
import { WarningIcon } from "affinityIcons";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { styled } from "styled-components";

import { Table } from "affinityUI/data/table";
import { DateTime } from "affinityUI/data/valueDisplay";
import { Grid } from "affinityUI/elements/grid";
import { Tag, TagType } from "affinityUI/elements/tag";
import { Restricted } from "lib/auth";
import { ManualResult, TestResult } from "types/patient";

import { ResultWrap } from "./resultWrap";

const ResultAlert = styled.div`
  color: ${({ theme }) => theme.colours.red[700]};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const isManualResult = (result: TestResult): result is ManualResult => result.__typename === "ManualResult";

const getResultCells = (result: TestResult, neutrophilThreshold?: Maybe<number>): React.ReactElement => {
  const manualResult = isManualResult(result);
  const DateCell = isManualResult(result) ? (
    <Grid $tight $cols={["min-content", "min-content"]}>
      <DateTime format={DateTime.FORMAT.DATE} value={result.dateRecorded} />
      <ResultWrap highlight>M</ResultWrap>
    </Grid>
  ) : (
    <DateTime format={DateTime.FORMAT.DATE} value={result.dateRecorded} />
  );
  return (
    <>
      <td
        className="no-wrap"
        scope="row"
        data-testid={format(new Date(result.dateRecorded), "dd-MM-yyyy HH:mm", { locale: enGB })}
      >
        {DateCell}
      </td>
      <td className="no-wrap">
        <DateTime format={DateTime.FORMAT.TIME} value={result.dateRecorded} />
      </td>
      <td>
        <ResultWrap
          highlight={manualResult}
          warning={Boolean(result.whiteBloodCells.measurementInterval?.outOfRange)}
          aria-label="WBC"
        >
          {result.whiteBloodCells.value}
        </ResultWrap>
      </td>
      <td>
        {result.belowNeutrophilThreshold && Boolean(neutrophilThreshold) ? (
          <ResultAlert>
            <ResultWrap
              highlight={manualResult}
              warning={Boolean(result.neutrophils.measurementInterval?.outOfRange)}
              aria-label="Neutrophil count"
            >
              {result.neutrophils.value}
            </ResultWrap>
            <Tag type={TagType.negative} dark data-testid="confirmatory-test-alert">
              ≤ {neutrophilThreshold} <WarningIcon />
            </Tag>
          </ResultAlert>
        ) : (
          <ResultWrap
            highlight={manualResult}
            warning={Boolean(result.neutrophils.measurementInterval?.outOfRange)}
            aria-label="Neutrophil count"
          >
            {result.neutrophils.value}
          </ResultWrap>
        )}
      </td>
      <td>
        <ResultWrap
          highlight={manualResult}
          warning={Boolean(result.haemoglobin.measurementInterval?.outOfRange)}
          aria-label="Haemoglobin level"
        >
          {result.haemoglobin.value}
        </ResultWrap>
      </td>
      <td>
        <ResultWrap
          highlight={manualResult}
          warning={Boolean(result.platelets.measurementInterval?.outOfRange)}
          aria-label="Platelet count"
        >
          {result.platelets.value}
        </ResultWrap>
      </td>
    </>
  );
};
export const ResultRow: React.FC<{
  result: TestResult;
  neutrophilThreshold?: Maybe<number>;
}> = ({ result, neutrophilThreshold }) => {
  const rowCells = getResultCells(result, neutrophilThreshold);
  return isManualResult(result) ? (
    <Table.ExtendableRow
      key={result.id}
      data-testid={result.id}
      extension={
        <Grid $cols="repeat(3, max-content)">
          <strong>Manually added</strong>
          <strong>
            Updated by: {result.enteredUser.firstName} {result.enteredUser.lastName}
          </strong>
          <Restricted permits={["change_resultdata"]}>
            <Link to={`result/${result.id}`}>Edit</Link>
          </Restricted>
        </Grid>
      }
    >
      {rowCells}
    </Table.ExtendableRow>
  ) : (
    <tr key={result.id} data-testid={result.id}>
      {rowCells}
    </tr>
  );
};
